import { type ReactNode } from 'react';

import {
    formatSotOffset,
    formatSotSpeaker,
    isAfterTransferBoundary,
    isPostTransferSotTurn,
    type SotTranscriptTurnDisplay,
} from '@apps/web/components/voice_calls/sot_transcript_display';

const TRANSCRIPT_SPEAKER_LABEL_PATTERN =
    /(^|\s)([A-Z][A-Za-z0-9'-]*(?:\s+[A-Za-z0-9][A-Za-z0-9'-]*){0,2}):/g;

export type TranscriptTurnRow = {
    readonly key: string;
    readonly speakerLabel: string;
    readonly text: string;
    readonly timestampLabel: string | null;
    readonly timestampIsApproximate: boolean;
    // True on the first turn after the call was transferred, so the
    // transcript can render a transfer marker at the handoff point.
    readonly isPostTransferStart: boolean;
};

export function normalizeCallTranscriptText(value: string | null | undefined) {
    const normalized = value?.replace(/\\n/g, '\n').trim();
    return normalized || null;
}

export function formatStructuredSotTranscriptText(
    turns: ReadonlyArray<SotTranscriptTurnDisplay> | null | undefined,
    postTransferSotStartMs: number | null,
) {
    const text = turns
        ?.map((turn) => {
            const turnText = turn.text.trim();
            if (!turnText) {
                return '';
            }

            const speakerLabel = formatSotSpeaker(turn.speaker, {
                postTransfer: isPostTransferSotTurn(
                    turn,
                    postTransferSotStartMs,
                ),
            });
            return `${speakerLabel}: ${turnText}`;
        })
        .filter((turnText) => turnText.length > 0)
        .join('\n\n');

    return normalizeCallTranscriptText(text);
}

export function getSpeakerLabelsFromTranscriptText(
    transcriptText: string | null,
): Array<string> {
    if (!transcriptText) {
        return [];
    }

    return Array.from(
        transcriptText.matchAll(TRANSCRIPT_SPEAKER_LABEL_PATTERN),
    ).map((match) => match[2] ?? '');
}

export function buildStructuredTranscriptRows({
    postTransferSotStartMs,
    showTransferMarker = false,
    speakerLabels,
    turns,
}: {
    readonly postTransferSotStartMs: number | null;
    readonly showTransferMarker?: boolean;
    readonly speakerLabels: ReadonlyArray<string>;
    readonly turns: ReadonlyArray<SotTranscriptTurnDisplay>;
}): Array<TranscriptTurnRow> {
    const rows: Array<TranscriptTurnRow> = [];
    let postTransferStartMarked = false;

    turns.forEach((turn, index) => {
        const text = turn.text.trim();
        if (!text) {
            return;
        }
        const afterTransferBoundary = isAfterTransferBoundary(
            turn,
            postTransferSotStartMs,
        );
        const isPostTransferStart =
            showTransferMarker &&
            afterTransferBoundary &&
            !postTransferStartMarked;
        if (isPostTransferStart) {
            postTransferStartMarked = true;
        }

        rows.push({
            key: `${turn.speaker}-${turn.start_ms ?? index}-${index}`,
            speakerLabel:
                speakerLabels[rows.length] ||
                formatSotSpeaker(turn.speaker, {
                    postTransfer: isPostTransferSotTurn(
                        turn,
                        postTransferSotStartMs,
                    ),
                }),
            text,
            timestampLabel:
                typeof turn.start_ms === 'number'
                    ? formatSotOffset(turn.start_ms)
                    : null,
            timestampIsApproximate: false,
            isPostTransferStart,
        });
    });

    return rows;
}

function getApproximateTranscriptTimestampLabel({
    durationMs,
    index,
    rowCount,
}: {
    readonly durationMs: number | null | undefined;
    readonly index: number;
    readonly rowCount: number;
}) {
    if (index === 0) {
        return formatSotOffset(0);
    }

    if (typeof durationMs !== 'number' || durationMs <= 0 || rowCount <= 1) {
        return null;
    }

    return formatSotOffset(Math.floor((durationMs * index) / rowCount));
}

export function buildPlainSpeakerTranscriptRows({
    durationMs,
    transcriptText,
}: {
    readonly durationMs: number | null | undefined;
    readonly transcriptText: string | null;
}): Array<TranscriptTurnRow> {
    if (!transcriptText) {
        return [];
    }

    const speakerMatches = Array.from(
        transcriptText.matchAll(TRANSCRIPT_SPEAKER_LABEL_PATTERN),
    );

    return speakerMatches
        .map((match, index) => {
            const speaker = match[2] ?? '';
            const prefix = match[1] ?? '';
            const labelStart = (match.index ?? 0) + prefix.length;
            const labelEnd = labelStart + speaker.length + 1;
            const nextMatch = speakerMatches[index + 1];
            const nextPrefix = nextMatch?.[1] ?? '';
            const nextLabelStart = nextMatch
                ? (nextMatch.index ?? transcriptText.length) + nextPrefix.length
                : transcriptText.length;
            const text = transcriptText.slice(labelEnd, nextLabelStart).trim();

            if (!speaker || !text) {
                return null;
            }

            return {
                key: `plain-${labelStart}-${index}`,
                speakerLabel: speaker,
                text,
                timestampLabel: getApproximateTranscriptTimestampLabel({
                    durationMs,
                    index,
                    rowCount: speakerMatches.length,
                }),
                timestampIsApproximate: true,
                isPostTransferStart: false,
            };
        })
        .filter((row): row is TranscriptTurnRow => row !== null);
}

export function TranscriptTurnTimestamp({
    turn,
}: {
    readonly turn: TranscriptTurnRow;
}) {
    if (!turn.timestampLabel) {
        return null;
    }

    const label = turn.timestampIsApproximate
        ? `~${turn.timestampLabel}`
        : turn.timestampLabel;

    return (
        <span
            aria-label={
                turn.timestampIsApproximate
                    ? `Approximate timestamp ${turn.timestampLabel}`
                    : `Timestamp ${turn.timestampLabel}`
            }
            className="text-muted-foreground justify-self-start font-mono text-xs tabular-nums sm:justify-self-end"
            title={turn.timestampIsApproximate ? 'Approximate timestamp' : ''}
        >
            {label}
        </span>
    );
}

export function renderTranscriptWithSpeakerLabels(
    transcriptText: string,
): Array<ReactNode> {
    const nodes: Array<ReactNode> = [];
    let lastIndex = 0;

    for (const match of transcriptText.matchAll(
        TRANSCRIPT_SPEAKER_LABEL_PATTERN,
    )) {
        const matchIndex = match.index ?? 0;
        const prefix = match[1] ?? '';
        const speaker = match[2] ?? '';
        const labelStart = matchIndex + prefix.length;
        const labelEnd = labelStart + speaker.length + 1;

        if (labelStart > lastIndex) {
            nodes.push(transcriptText.slice(lastIndex, labelStart));
        }

        nodes.push(
            <strong
                className="text-foreground font-semibold"
                key={`speaker-${labelStart}`}
            >
                {speaker}:
            </strong>,
        );
        lastIndex = labelEnd;
    }

    if (lastIndex < transcriptText.length) {
        nodes.push(transcriptText.slice(lastIndex));
    }

    return nodes.length > 0 ? nodes : [transcriptText];
}
